import React from 'react';
import Skeleton from './Skeleton';

/**
 * ComplianceSkeleton
 * Shimmer placeholder for the Staff Compliance page:
 *   - header (title + action buttons)
 *   - 4 summary stat tiles
 *   - filter bar + staff rows, each with two check-cards
 * Built from the base <Skeleton /> block (pure Tailwind shimmer).
 */

const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

const S = Skeleton;

const TileSkeleton = () => (
  <div className={`${CARD} p-5`}>
    <div className="flex items-start justify-between">
      <S className="h-3 w-20 rounded-md" />
      <S className="w-11 h-11 rounded-2xl" />
    </div>
    <S className="h-8 w-16 rounded-lg mt-3" />
    <S className="h-3 w-24 rounded-md mt-3" />
  </div>
);

const CheckCardSkeleton = () => (
  <div className="flex-1 min-w-[240px] border border-[#EEF2F8] dark:border-gray-800 rounded-2xl p-4">
    <div className="flex items-center justify-between">
      <S className="h-3 w-24 rounded-md" />
      <S className="h-4 w-14 rounded" />
    </div>
    <S className="h-4 w-32 rounded-md mt-3" />
    <S className="h-3 w-40 rounded-md mt-2" />
    <S className="h-9 w-full rounded-lg mt-3" />
    <S className="h-8 w-full rounded-lg mt-3" />
  </div>
);

const RowSkeleton = () => (
  <div className="flex flex-col lg:flex-row lg:items-center gap-4 px-6 py-5">
    <div className="w-full lg:w-[200px] shrink-0 space-y-2">
      <S className="h-4 w-32 rounded-md" />
      <S className="h-3 w-24 rounded-md" />
    </div>
    <div className="flex-1 flex flex-col sm:flex-row gap-3">
      <CheckCardSkeleton />
      <CheckCardSkeleton />
    </div>
    <div className="w-full lg:w-[180px] flex flex-col gap-2 lg:items-end">
      <S className="h-9 w-full lg:w-[130px] rounded-lg" />
      <S className="h-3 w-28 rounded-md" />
    </div>
  </div>
);

const ComplianceSkeleton = () => (
  <div className="w-full space-y-5 pb-12">
    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <S className="h-7 w-56 rounded-xl" />
        <S className="h-3.5 w-80 max-w-full rounded-md mt-3" />
      </div>
      <div className="flex gap-2">
        <S className="h-[42px] w-24 rounded-xl" />
        <S className="h-[42px] w-28 rounded-xl" />
        <S className="h-[42px] w-24 rounded-xl" />
      </div>
    </div>

    {/* Summary tiles */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => <TileSkeleton key={i} />)}
    </div>

    {/* Filters + list */}
    <div className={CARD}>
      <div className="p-4 border-b border-[#EEF2F8] dark:border-gray-800 flex flex-wrap items-center gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <S key={i} className="h-9 w-[150px] rounded-xl" />
        ))}
      </div>
      <div className="divide-y divide-[#EEF2F8]">
        {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
      </div>
    </div>
  </div>
);

export default ComplianceSkeleton;
